// react/libs
import { useCallback, useEffect, useRef } from 'react';
import dayjs, { type Dayjs } from 'dayjs';

// types
import { EntityCalendarPositionTrackerProps } from './EntityCalendarPositionTracker.types';

// stores
import { useCalendarNavigationStore } from 'components/mrp/components/CalendarContainer/components/store/calendarNavigationStore.store';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';

// components
import { Box } from '@mui/material';

// consts
import { SCROLL_CONTAINER_CLASS } from 'components/mrp/components/CalendarContainer/CalendarContainer.consts';

// utils
import { scrollIntoView } from './EntityCalendarPositionTracker.utils';

export const EntityCalendarPositionTracker = ({
  children,
  calendarId
}: EntityCalendarPositionTrackerProps) => {
  const selfRef = useRef<HTMLDivElement | null>(null);

  const position = useCalendarNavigationStore((state) => state.position);
  const appointment = useAppointmentStore((state) => state.appointment);
  const timeStep = useCalendarStore((state) => state.timeStep);

  const snapToTimeSlot = useCallback(
    (date: Dayjs) => {
      return date
        .startOf('minute')
        .subtract(date.minute() % timeStep, 'minute');
    },
    [timeStep]
  );

  useEffect(() => {
    const activeTimeslotGroup = selfRef.current?.querySelector(
      '.rbc-timeslot-group.is-active'
    );

    if (activeTimeslotGroup) {
      const timeslot = activeTimeslotGroup.closest('.time-slot-cell-container');
      const activeCellDate = dayjs(timeslot?.getAttribute('time-stamp'));

      const shouldDeselect =
        !!appointment ||
        !position ||
        calendarId !== position.calendarId ||
        !activeCellDate.isSame(snapToTimeSlot(position.date));

      if (shouldDeselect) {
        activeTimeslotGroup.classList.remove('is-active');
      }
    }

    if (!!appointment || position?.calendarId !== calendarId) return;

    const snappedDate = snapToTimeSlot(position.date);
    const currentCell = selfRef.current?.querySelector(
      `[data-time-stamp='${snappedDate.toISOString()}']`
    );
    if (!currentCell) return;

    currentCell.parentElement?.classList.add('is-active');
    const scrollContainer = currentCell?.closest(`.${SCROLL_CONTAINER_CLASS}`);

    // Default "Element.scrollIntoView(..)" implementation required styling
    // that conflicted with RBC and our own sticky scroll-container, so we
    // manuelly update the scroll state here.
    scrollIntoView({
      scrollContainer: scrollContainer!,
      timeSlotRef: currentCell!
    });
  }, [position, appointment, calendarId, snapToTimeSlot]);

  return <Box ref={selfRef}>{children}</Box>;
};

export default EntityCalendarPositionTracker;
